import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, Text, VStack, HStack, Card, CardBody, SimpleGrid, Stat, StatLabel, StatNumber,
  Spinner, Button, useToast, Table, Thead, Tbody, Tr, Th, Td, TableContainer, Badge, 
  Icon, Input, InputGroup, InputLeftElement, Select, Collapse, Avatar, Flex
} from '@chakra-ui/react';
import { FiTarget, FiSearch, FiFilter, FiChevronDown, FiChevronRight, FiCalendar, FiRefreshCw } from 'react-icons/fi';
import BugTicketDetails from './BugTicketDetails';
import useAnalyticsAPI from '../hooks/useAnalyticsAPI';

export default function TicketsTab() {
  const [allTicketsData, setAllTicketsData] = useState([]); // Full dataset
  const [filteredTicketsData, setFilteredTicketsData] = useState([]); // Filtered dataset
  const [loading, setLoading] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Fixed at 10 rows per page
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  
  // Expanded rows state
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  const toast = useToast();
  const { makeRequest } = useAnalyticsAPI();

  // Fetch all tickets data from the detailed endpoint
  const fetchAllTicketsData = async () => {
    try {
      setLoading(true);
      
      console.log('Fetching all tickets data from detailed endpoint...');
      
      // Show loading progress
      toast({
        title: "Loading Tickets",
        description: "Fetching all tickets in batches...",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      
      // Fetch all tickets in batches of 100 (API limit)
      let allAnalyses = [];
      let page = 1;
      let hasMoreData = true;
      
      while (hasMoreData) {
        console.log(`Fetching page ${page}...`);
        const result = await makeRequest(`/api/bugs/analytics/detailed?page=${page}&limit=100`);
        
        if (result.status === 'success' && result.data && result.data.analyses) {
          allAnalyses = allAnalyses.concat(result.data.analyses);
          console.log(`Page ${page}: ${result.data.analyses.length} tickets, Total so far: ${allAnalyses.length}`);
          
          // Check if we've fetched all data
          if (result.data.analyses.length < 100 || allAnalyses.length >= result.data.totalAnalyses) {
            hasMoreData = false;
          } else {
            page++;
            // Add a small delay to prevent overwhelming the API
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        } else {
          console.log(`Page ${page} failed or no data`);
          hasMoreData = false;
        }
      }
      
      console.log(`Fetched ${allAnalyses.length} tickets across ${page} pages`);
      
      // Show success message
      toast({
        title: "Tickets Loaded",
        description: `Successfully loaded ${allAnalyses.length} tickets`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
      // Create a mock result object with all the analyses
      const result = {
        status: 'success',
        data: {
          analyses: allAnalyses,
          totalAnalyses: allAnalyses.length
        }
      };
      
      console.log('Detailed API Response:', result);
      
      if (result.status === 'success' && result.data) {
        let allTicketsData = [];
        
        // Process the detailed data
        if (result.data.analyses && Array.isArray(result.data.analyses)) {
          // If the response has an analyses property (current API structure)
          allTicketsData = result.data.analyses.map(analysis => ({
            // Only include essential fields for the table display
            id: analysis.issueKey || `TICKET-${Math.random().toString(36).substr(2, 9)}`,
            issueKey: analysis.issueKey, // Preserve the original issueKey for AI analysis
            title: analysis.issueKey || 'No Title',
            module: analysis.module || analysis.moduleName || (() => {
              if (analysis.allCategories) {
                if (typeof analysis.allCategories === 'string') {
                  return analysis.allCategories;
                }
                if (typeof analysis.allCategories === 'object' && analysis.allCategories !== null) {
                  // Extract module names from allCategories object
                  const moduleNames = Object.keys(analysis.allCategories);
                  if (moduleNames.length > 0) {
                    return moduleNames.join(', ');
                  }
                }
              }
              return 'Unknown Module';
            })(),
            severity: analysis.severity || 'Medium',
            status: 'Open', // Default status since it's not in the analysis
            assignee: 'Unassigned', // Default assignee
            createdAt: new Date().toISOString(), // Default date
            updatedAt: new Date().toISOString(), // Default date
            description: `Issue ${analysis.issueKey} - ${analysis.primaryRootCause || 'No description'}`,
            rootCause: analysis.primaryRootCause || 'Unknown',
            recurrenceProbability: analysis.rootCauseScore || 50,
            client: (() => {
              if (analysis.leaveSystemAnalysis?.customerImpactAnalysis?.affectedCustomers && Array.isArray(analysis.leaveSystemAnalysis.customerImpactAnalysis.affectedCustomers)) {
                return analysis.leaveSystemAnalysis.customerImpactAnalysis.affectedCustomers.join(', ');
              }
              if (analysis.client) {
                return analysis.client;
              }
              if (analysis.clientAffected) {
                return analysis.clientAffected;
              }
              if (analysis.clientImpact) {
                if (typeof analysis.clientImpact === 'string') {
                  return analysis.clientImpact;
                }
                if (typeof analysis.clientImpact === 'object' && analysis.clientImpact !== null) {
                  if (analysis.clientImpact.clientNames && Array.isArray(analysis.clientImpact.clientNames)) {
                    return analysis.clientImpact.clientNames.join(', ');
                  }
                  if (analysis.clientImpact.name) {
                    return analysis.clientImpact.name;
                  }
                  return JSON.stringify(analysis.clientImpact);
                }
              }
              return 'Unknown Client';
            })(),
            // Store minimal data for AI analysis
            _aiAnalysisData: null, // Will be loaded on demand
            _hasAIAnalysis: false // Flag to track if AI analysis is available
          }));
        } else if (Array.isArray(result.data)) {
          // If the response is a direct array of tickets
          allTicketsData = result.data.map(ticket => ({
            ...ticket,
            // Ensure all required fields are present with fallbacks
            id: ticket.issueKey || ticket.id || `TICKET-${Math.random().toString(36).substr(2, 9)}`,
            title: ticket.title || ticket.summary || ticket.issueKey || 'No Title',
            module: ticket.module || 'Unknown Module',
            severity: ticket.severity || 'Medium',
            status: ticket.status || 'Open',
            assignee: ticket.assignee || 'Unassigned',
            createdAt: ticket.createdAt || new Date().toISOString(),
            updatedAt: ticket.updatedAt || new Date().toISOString(),
            description: ticket.description || `Issue ${ticket.issueKey} in ${ticket.module}`,
            rootCause: ticket.rootCause || 'Unknown',
            recurrenceProbability: ticket.recurrenceProbability || 50
          }));
        } else if (result.data.tickets && Array.isArray(result.data.tickets)) {
          // If the response has a tickets property
          allTicketsData = result.data.tickets.map(ticket => ({
            ...ticket,
            id: ticket.issueKey || ticket.id || `TICKET-${Math.random().toString(36).substr(2, 9)}`,
            title: ticket.title || ticket.summary || ticket.issueKey || 'No Title',
            module: ticket.module || 'Unknown Module',
            severity: ticket.severity || 'Medium',
            status: ticket.status || 'Open',
            assignee: ticket.assignee || 'Unassigned',
            createdAt: ticket.createdAt || new Date().toISOString(),
            updatedAt: ticket.updatedAt || new Date().toISOString(),
            description: ticket.description || `Issue ${ticket.issueKey} in ${ticket.module}`,
            rootCause: ticket.rootCause || 'Unknown',
            recurrenceProbability: ticket.recurrenceProbability || 50
          }));
        } else if (result.data.data && Array.isArray(result.data.data)) {
          // If the response has a data property
          allTicketsData = result.data.data.map(ticket => ({
            ...ticket,
            id: ticket.issueKey || ticket.id || `TICKET-${Math.random().toString(36).substr(2, 9)}`,
            title: ticket.title || ticket.summary || ticket.issueKey || 'No Title',
            module: ticket.module || 'Unknown Module',
            severity: ticket.severity || 'Medium',
            status: ticket.status || 'Open',
            assignee: ticket.assignee || 'Unassigned',
            createdAt: ticket.createdAt || new Date().toISOString(),
            updatedAt: ticket.updatedAt || new Date().toISOString(),
            description: ticket.description || `Issue ${ticket.issueKey} in ${ticket.module}`,
            rootCause: ticket.rootCause || 'Unknown',
            recurrenceProbability: ticket.recurrenceProbability || 50
          }));
        }
        
        console.log('All tickets data loaded:', allTicketsData.length, 'tickets');
        console.log('Total analyses available:', result.data.totalAnalyses);
        setAllTicketsData(allTicketsData);
        
        toast({
          title: "Tickets Data Loaded",
          description: `Loaded ${allTicketsData.length} tickets from detailed endpoint`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        console.log('No tickets data in response');
        setAllTicketsData([]);
      }
      
    } catch (error) {
      console.error('Failed to fetch tickets data:', error);
      setAllTicketsData([]);
      toast({
        title: "Error",
        description: `Failed to load tickets data: ${error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Apply filters to the full dataset
  const applyFilters = useMemo(() => {
    let filtered = [...allTicketsData];
    
    console.log('Applying ticket filters:', { searchTerm, selectedModule, selectedClient, selectedSeverity, selectedStatus });
    console.log('Initial tickets count:', filtered.length);
    
    // Search filter (case-insensitive) - search in title, id, and description
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(ticket => {
        const title = ticket.title?.toLowerCase() || '';
        const id = ticket.id?.toLowerCase() || '';
        const description = ticket.description?.toLowerCase() || '';
        
        const matches = title.includes(searchLower) || 
                       id.includes(searchLower) || 
                       description.includes(searchLower);
        
        console.log(`Ticket "${ticket.id}" matches search "${searchTerm}": ${matches}`);
        return matches;
      });
      console.log('After search filter:', filtered.length, 'tickets');
    }
    
    // Module filter
    if (selectedModule && selectedModule.trim()) {
      filtered = filtered.filter(ticket => {
        const matches = ticket.module === selectedModule;
        console.log(`Ticket "${ticket.id}" module "${ticket.module}" === "${selectedModule}": ${matches}`);
        return matches;
      });
      console.log('After module filter:', filtered.length, 'tickets');
    }
    
    // Client filter
    if (selectedClient && selectedClient.trim()) {
      filtered = filtered.filter(ticket => {
        const matches = ticket.client === selectedClient;
        console.log(`Ticket "${ticket.id}" client "${ticket.client}" === "${selectedClient}": ${matches}`);
        return matches;
      });
      console.log('After client filter:', filtered.length, 'tickets');
    }
    
    // Severity filter
    if (selectedSeverity && selectedSeverity.trim()) {
      filtered = filtered.filter(ticket => {
        const matches = ticket.severity === selectedSeverity;
        console.log(`Ticket "${ticket.id}" severity "${ticket.severity}" === "${selectedSeverity}": ${matches}`);
        return matches;
      });
      console.log('After severity filter:', filtered.length, 'tickets');
    }
    
    // Status filter
    if (selectedStatus && selectedStatus.trim()) {
      filtered = filtered.filter(ticket => {
        const matches = ticket.status === selectedStatus;
        console.log(`Ticket "${ticket.id}" status "${ticket.status}" === "${selectedStatus}": ${matches}`);
        return matches;
      });
      console.log('After status filter:', filtered.length, 'tickets');
    }
    
    console.log('Final filtered tickets count:', filtered.length, 'tickets');
    return filtered;
  }, [allTicketsData, searchTerm, selectedModule, selectedClient, selectedSeverity, selectedStatus]);

  // Update filtered data when filters change
  useEffect(() => {
    setFilteredTicketsData(applyFilters);
    setCurrentPage(1); // Reset to first page when filters change
  }, [applyFilters]);

  // Calculate pagination with performance optimization
  const totalPages = Math.ceil(filteredTicketsData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = filteredTicketsData.slice(startIndex, endIndex);
  
  // Performance optimization: Only render visible rows
  const visibleTickets = currentPageData.slice(0, Math.min(itemsPerPage, 50)); // Limit to 50 visible at once
  
  const hasNext = currentPage < totalPages;
  const hasPrev = currentPage > 1;

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Close all expanded rows when changing pages
    setExpandedRows(new Set());
  };

  const handleNextPage = () => {
    if (hasNext) {
      setCurrentPage(currentPage + 1);
      setExpandedRows(new Set());
    }
  };

  const handlePrevPage = () => {
    if (hasPrev) {
      setCurrentPage(currentPage - 1);
      setExpandedRows(new Set());
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedModule('');
    setSelectedClient('');
    setSelectedSeverity('');
    setSelectedStatus('');
  };

  // State for AI analysis data
  const [aiAnalysisData, setAiAnalysisData] = useState({});
  const [loadingAI, setLoadingAI] = useState(new Set());
  


  // Toggle row expansion and fetch AI analysis
  const toggleRowExpansion = async (ticket) => {
    const ticketId = ticket.id || ticket.issueKey;
    const newExpandedRows = new Set(expandedRows);
    
    if (newExpandedRows.has(ticketId)) {
      // Collapse row
      newExpandedRows.delete(ticketId);
      setExpandedRows(newExpandedRows);
    } else {
      // Expand row and fetch AI analysis
      newExpandedRows.add(ticketId);
      setExpandedRows(newExpandedRows);
      
      // Only fetch AI analysis if we don't have it already
      if (!aiAnalysisData[ticketId]) {
        await fetchAIAnalysis(ticket);
      }
    }
  };

  // Fetch AI analysis for a specific ticket
  const fetchAIAnalysis = async (ticket) => {
    const ticketId = ticket.id;
    const issueKey = ticket.issueKey || ticket.id;
    
    if (!issueKey) {
      console.error('No issueKey found for ticket:', ticket);
      return;
    }

    // Check if we already have the AI analysis data
    if (ticket._aiAnalysisData) {
      setAiAnalysisData(prev => ({
        ...prev,
        [ticketId]: ticket._aiAnalysisData
      }));
      return;
    }

    try {
      setLoadingAI(prev => new Set(prev).add(ticketId));
      
      // Add a timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 30000)
      );
      
      const result = await Promise.race([
        makeRequest(`/api/bugs/analytics/ai-analysis/${issueKey}`),
        timeoutPromise
      ]);
      
      if (result.status === 'success' && result.data) {
        // The AI analysis endpoint returns { aiAnalysis: {...}, issue: {...} }
        const aiAnalysisData = result.data.aiAnalysis || result.data;
        const issueData = result.data.issue || {};
        
        // Map the API fields to what the component expects
        const mappedAnalysisData = {
          ...aiAnalysisData,
          // Map API fields to component expected fields
          aiSummary: aiAnalysisData.summary,
          rootCause: aiAnalysisData.primaryRootCause,
          impact: aiAnalysisData.businessImpact,
          recurrenceProbability: aiAnalysisData.rootCauseScore,
          // Keep original fields as fallbacks
          summary: aiAnalysisData.summary,
          primaryRootCause: aiAnalysisData.primaryRootCause,
          businessImpact: aiAnalysisData.businessImpact,
          rootCauseScore: aiAnalysisData.rootCauseScore,
          issue: issueData
        };
        
        setAiAnalysisData(prev => ({
          ...prev,
          [ticketId]: mappedAnalysisData
        }));
        
        toast({
          title: "AI Analysis Loaded",
          description: `Loaded AI analysis for ${issueKey}`,
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      } else {
        console.log('No AI analysis data in response');
        setAiAnalysisData(prev => ({
          ...prev,
          [ticketId]: null
        }));
      }
      
    } catch (error) {
      console.error('Failed to fetch AI analysis:', error);
      setAiAnalysisData(prev => ({
        ...prev,
        [ticketId]: null
      }));
      
      toast({
        title: "AI Analysis Failed",
        description: `Failed to load AI analysis for ${issueKey}: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingAI(prev => {
        const newSet = new Set(prev);
        newSet.delete(ticketId);
        return newSet;
      });
    }
  };

  // Get unique values for filter options
  const modules = useMemo(() => {
    const uniqueModules = [...new Set(allTicketsData.map(ticket => ticket.module))];
    return uniqueModules.filter(Boolean).sort();
  }, [allTicketsData]);

  const clients = useMemo(() => {
    const uniqueClients = [...new Set(allTicketsData.map(ticket => ticket.client))];
    return uniqueClients.filter(Boolean).sort();
  }, [allTicketsData]);

  const severities = useMemo(() => {
    const uniqueSeverities = [...new Set(allTicketsData.map(ticket => ticket.severity))];
    return uniqueSeverities.filter(Boolean).sort();
  }, [allTicketsData]);

  const statuses = useMemo(() => {
    const uniqueStatuses = [...new Set(allTicketsData.map(ticket => ticket.status))];
    return uniqueStatuses.filter(Boolean).sort();
  }, [allTicketsData]);

  useEffect(() => {
    fetchAllTicketsData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'yellow';
      case 'low': return 'green';
      default: return 'gray';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'done': return 'green';
      case 'in progress': return 'blue';
      case 'validated by qa': return 'purple';
      case 'qa': return 'cyan';
      case 'open': return 'orange';
      case 'closed': return 'gray';
      default: return 'gray';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  if (loading && allTicketsData.length === 0) {
    return (
      <VStack py={12} spacing={4}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading tickets data...</Text>
        <Text fontSize="sm" color="gray.500">
          Fetching all tickets in batches (API limit: 100 per request)
        </Text>
        <Text fontSize="xs" color="gray.400">
          This may take a few moments for large datasets
        </Text>
      </VStack>
    );
  }

  if (allTicketsData.length === 0) {
    return (
      <VStack spacing={6} align="stretch">
        <Card>
          <CardBody>
            <VStack spacing={4} py={12} textAlign="center">
              <Icon as={FiTarget} size="3rem" color="gray.400" />
              <Text fontSize="xl" fontWeight="bold" color="gray.600">
                No Tickets Data Available
              </Text>
              <Text color="gray.500" maxW="md">
                No tickets data found. This could mean:
                <br />• Tickets are still being processed by the AI system
                <br />• No tickets have been submitted for analysis
                <br />• The API is not returning ticket data
              </Text>
              <HStack spacing={4}>
                <Button
                  leftIcon={<Icon as={FiTarget} />}
                  colorScheme="blue"
                  variant="outline"
                  onClick={fetchAllTicketsData}
                  isLoading={loading}
                >
                  Retry Loading
                </Button>
                <Button
                  leftIcon={<Icon as={FiRefreshCw} />}
                  colorScheme="orange"
                  variant="outline"
                  onClick={() => {
                    toast({
                      title: "Processing Status",
                      description: "Check the 'Process All Tickets' button on the Overview tab to see processing status",
                      status: "info",
                      duration: 5000,
                      isClosable: true,
                    });
                  }}
                >
                  Check Processing Status
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header Stats */}
      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Total Tickets</StatLabel>
              <StatNumber color="blue.500">{allTicketsData.length}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Filtered Results</StatLabel>
              <StatNumber color="green.500">{filteredTicketsData.length}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Critical Issues</StatLabel>
              <StatNumber color="red.500">
                {allTicketsData.filter(ticket => ticket.severity?.toLowerCase() === 'critical').length}
              </StatNumber>
            </Stat>
          </CardBody>
        </Card>
        
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Resolved</StatLabel>
              <StatNumber color="green.500">
                {allTicketsData.filter(ticket => ticket.status?.toLowerCase() === 'done').length}
              </StatNumber>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Filter Options */}
      <Card>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <HStack>
              <Icon as={FiFilter} color="blue.500" />
              <Text fontSize="lg" fontWeight="bold">Filter Options</Text>
            </HStack>
            
            <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4}>
              {/* Search Filter */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>Search Tickets</Text>
                <InputGroup>
                  <InputLeftElement>
                    <Icon as={FiSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search by title, ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Box>
              
              {/* Module Filter */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>Module</Text>
                <Select
                  placeholder="All Modules"
                  value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                >
                  {modules.map(module => (
                    <option key={module} value={module}>{module}</option>
                  ))}
                </Select>
              </Box>
              
              {/* Client Filter */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>Client</Text>
                <Select
                  placeholder="All Clients"
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                >
                  {clients.map(client => (
                    <option key={client} value={client}>{client}</option>
                  ))}
                </Select>
              </Box>
              
              {/* Severity Filter */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>Severity</Text>
                <Select
                  placeholder="All Severities"
                  value={selectedSeverity}
                  onChange={(e) => setSelectedSeverity(e.target.value)}
                >
                  {severities.map(severity => (
                    <option key={severity} value={severity}>{severity}</option>
                  ))}
                </Select>
              </Box>
              
              {/* Status Filter */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>Status</Text>
                <Select
                  placeholder="All Statuses"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  {statuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </Select>
              </Box>
            </SimpleGrid>
            
            {/* Clear Filters Button */}
            {(searchTerm || selectedModule || selectedClient || selectedSeverity || selectedStatus) && (
              <Button
                variant="outline"
                colorScheme="gray"
                onClick={clearFilters}
                size="sm"
                alignSelf="flex-start"
              >
                Clear All Filters
              </Button>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Tickets Table */}
      <Card>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <HStack justify="space-between">
              <HStack>
                <Icon as={FiTarget} color="red.500" />
                <Text fontSize="lg" fontWeight="bold">Bug Tickets</Text>
                <Badge colorScheme="blue">{filteredTicketsData.length} tickets</Badge>
              </HStack>
              <Text fontSize="sm" color="gray.500">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredTicketsData.length)} of {filteredTicketsData.length} tickets
              </Text>
            </HStack>
            
            {/* Table */}
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Ticket</Th>
                    <Th>Module</Th>
                    <Th>Client</Th>
                    <Th>Severity</Th>
                    <Th>Status</Th>
                    <Th>Assignee</Th>
                    <Th>Created</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {visibleTickets.map((ticket) => (
                    <React.Fragment key={ticket.id}>
                      <Tr 
                        cursor="pointer" 
                        onClick={() => toggleRowExpansion(ticket)}
                        _hover={{ bg: "gray.50" }}
                      >
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="bold" color="blue.600">{ticket.id}</Text>
                            <Text fontSize="sm" color="gray.600" noOfLines={2}>
                              {ticket.title}
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <Badge colorScheme="blue" variant="subtle" size="sm">
                            {ticket.module}
                          </Badge>
                        </Td>
                        <Td>
                          <Text fontSize="sm">{ticket.client}</Text>
                        </Td>
                        <Td>
                          <Badge colorScheme={getSeverityColor(ticket.severity)} size="sm">
                            {ticket.severity}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(ticket.status)} size="sm">
                            {ticket.status}
                          </Badge>
                        </Td>
                        <Td>
                          <HStack spacing={2}>
                            <Avatar size="xs" name={ticket.assignee} bg="blue.500" color="white">
                              {getInitials(ticket.assignee)}
                            </Avatar>
                            <Text fontSize="sm">{ticket.assignee}</Text>
                          </HStack>
                        </Td>
                        <Td>
                          <HStack spacing={1}>
                            <Icon as={FiCalendar} size="xs" color="gray.500" />
                            <Text fontSize="sm" color="gray.600">
                              {formatDate(ticket.createdAt)}
                            </Text>
                          </HStack>
                        </Td>
                        <Td>
                          <HStack spacing={2}>
                            <Icon 
                              as={expandedRows.has(ticket.id) ? FiChevronDown : FiChevronRight} 
                              color="gray.500" 
                              cursor="pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleRowExpansion(ticket);
                              }}
                            />
                            {loadingAI.has(ticket.id) && (
                              <Spinner size="xs" color="blue.500" />
                            )}
                          </HStack>
                        </Td>
                      </Tr>
                      
                      {/* Expanded Row with AI Analysis */}
                      <Tr>
                        <Td colSpan={8} p={0}>
                          <Collapse in={expandedRows.has(ticket.id)} animateOpacity>
                            <Box p={4} bg="gray.50" borderTop="1px solid" borderColor="gray.200">
                              {loadingAI.has(ticket.id) ? (
                                <VStack spacing={4} py={8}>
                                  <Spinner size="lg" color="blue.500" />
                                  <Text>Loading AI analysis...</Text>
                                </VStack>
                              ) : aiAnalysisData[ticket.id] ? (
                                <BugTicketDetails 
                                  issueKey={ticket.id} 
                                  ticket={ticket} 
                                  aiAnalysis={aiAnalysisData[ticket.id]}
                                />
                              ) : aiAnalysisData[ticket.id] === null ? (
                                <Box textAlign="center" py={8}>
                                  <Text color="gray.500">No AI analysis available for this ticket.</Text>
                                  <Button
                                    size="sm"
                                    colorScheme="blue"
                                    variant="outline"
                                    onClick={() => fetchAIAnalysis(ticket)}
                                    mt={2}
                                  >
                                    Retry Loading AI Analysis
                                  </Button>
                                </Box>
                              ) : (
                                <Box textAlign="center" py={8}>
                                  <Text color="gray.500">Click to load AI analysis...</Text>
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </Td>
                      </Tr>
                    </React.Fragment>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Flex justify="center" align="center" spacing={4}>
                <Button
                  size="sm"
                  onClick={handlePrevPage}
                  isDisabled={!hasPrev}
                  variant="outline"
                >
                  Previous
                </Button>
                
                <HStack spacing={2} mx={4}>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <Button
                      key={page}
                      size="sm"
                      variant={currentPage === page ? "solid" : "outline"}
                      colorScheme={currentPage === page ? "blue" : "gray"}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Button>
                  ))}
                </HStack>
                
                <Button
                  size="sm"
                  onClick={handleNextPage}
                  isDisabled={!hasNext}
                  variant="outline"
                >
                  Next
                </Button>
              </Flex>
            )}
            


            {/* No Results Message */}
            {filteredTicketsData.length === 0 && (
              <Box textAlign="center" py={8}>
                <Text color="gray.500">No tickets match the current filters.</Text>
                <Button
                  variant="outline"
                  colorScheme="blue"
                  onClick={clearFilters}
                  mt={2}
                >
                  Clear Filters
                </Button>
              </Box>
            )}
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  );
}
